'use client'

import { useAuth } from '@clerk/nextjs'
import { useMemo } from 'react'

export const CONVEX_CLERK_JWT_TEMPLATE = 'convex' as const

type ClerkTokenGetter = (options: {
  template: typeof CONVEX_CLERK_JWT_TEMPLATE
  skipCache: boolean
}) => Promise<string | null>

type ConvexTokenFetcher = (options: {
  forceRefreshToken: boolean
}) => Promise<string | null>

/**
 * Build the token fetcher separately from React so the named-template
 * contract can be verified without mounting Clerk or opening a Convex socket.
 */
export function createNamedConvexTokenFetcher(
  getToken: ClerkTokenGetter,
  _claimScope?: Readonly<{
    orgId: string | null | undefined
    orgRole: string | null | undefined
  }>
): ConvexTokenFetcher {
  return async ({ forceRefreshToken }) => {
    try {
      return await getToken({
        template: CONVEX_CLERK_JWT_TEMPLATE,
        skipCache: forceRefreshToken,
      })
    } catch {
      // Convex's auth adapter treats a null token as unauthenticated and keeps
      // tenant queries fail-closed while Clerk refresh or configuration heals.
      return null
    }
  }
}

/**
 * Deterministic Clerk adapter for `ConvexProviderWithAuth`.
 *
 * Convex's stock Clerk adapter chooses the default Clerk session token when
 * that token advertises `aud === "convex"`; otherwise it chooses the named
 * `convex` JWT template. Brew's authorization claims intentionally live only
 * in the named template, so the choice must not depend on unrelated Clerk
 * integration state. Both Next apps use this hook and always request the
 * reviewed named template explicitly.
 */
export function useNamedConvexAuth() {
  const { getToken, isLoaded, isSignedIn, orgId, orgRole } = useAuth()
  const fetchAccessToken = useMemo(
    () => createNamedConvexTokenFetcher(getToken, { orgId, orgRole }),
    [getToken, orgId, orgRole]
  )

  return useMemo(
    () => ({
      isLoading: !isLoaded,
      isAuthenticated: isSignedIn ?? false,
      fetchAccessToken,
    }),
    [fetchAccessToken, isLoaded, isSignedIn]
  )
}
